import { ImageWithFallback } from "./ImageWithFallback";
import { useLanguage } from "../../i18n/LanguageContext";
import { useMemo } from "react";

type FacilityRow = {
  id: string;
  title: string;
  subtitle: string;
  desc: string;
  images: string[];
};

type FacilityCardProps = {
  item: FacilityRow;
  image: string;
  large?: boolean;
  isMobile?: boolean;
};

const FacilityCard = ({
  item,
  image,
  large = false,
  isMobile,
}: FacilityCardProps) => {
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl bg-white shadow-[0_12px_32px_rgba(0,0,0,0.10)] ${
        large ? "min-h-[420px] md:min-h-[500px]" : "min-h-[300px] md:min-h-[360px]"
      }`}
    >
      <ImageWithFallback
        src={image}
        alt={item.title}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 z-10 p-4 md:p-6 text-white">
        <p className="hero-highlight-text text-sm font-semibold leading-snug md:text-lg lg:text-xl  ">
          {isMobile ? item.subtitle : item.title}
        </p>

        {!isMobile && (
          <p className="mt-2 max-w-2xl text-sm leading-6 text-white/90 md:text-[15px]">
            {item.desc}
          </p>
        )}
      </div>
    </div>
  );
};

type MiniImageProps = {
  src: string;
  alt: string;
};

const MiniImage = ({ src, alt }: MiniImageProps) => {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-[0_10px_24px_rgba(0,0,0,0.10)]">
      <ImageWithFallback
        src={src}
        alt={alt}
        className="h-[180px] w-full object-cover md:h-[220px]"
      />
    </div>
  );
};

export const FacilitiesSection = ({ isMobile }: { isMobile?: boolean }) => {
  const { t } = useLanguage();

  const facilityRows: FacilityRow[] = useMemo(
    () => [
      {
        id: "equipment",
        title: t("facility1_title"),
        subtitle: t("facility1_subtitle"),
        desc: t("facility1_desc"),
        images: ["/a16/trang-thiet-bi-1.jpg", "/a16/trang-thiet-bi-2.jpg"],
      },
      {
        id: "room",
        title: t("facility2_title"),
        subtitle: t("facility2_subtitle"),
        desc: t("facility2_desc"),
        images: ["/a16/buong-benh1.jpg", "/a16/buong-benh-2-v2.jpg"],
      },
      {
        id: "clinic",
        title: t("facility3_title"),
        subtitle: t("facility3_subtitle"),
        desc: t("facility3_desc"),
        images: ["/a16/phong-kham-1-v2.jpg", "/a16/phong-kham-2-v2.jpg"],
      },
    ],
    [t]
  );

  return (
    <section className="relative bg-green-600 py-12 md:py-16">
      <div
        className="absolute left-0 right-0 top-0 h-[100px]"
        style={{
          backgroundImage: "url('/images/divider.svg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      />

      <div className="relative mx-auto max-w-6xl px-4 pt-12 md:px-6 lg:px-8">
        <div className="mx-auto mb-8 flex max-w-2xl flex-col items-center justify-center text-center md:mb-12">
          <p className="text-xl md:text-2xl font-semibold text-white">
            {t("cosovatchat")}
          </p>
          <div className="mt-3 h-1 w-[120px] rounded-full bg-white" />
        </div>

        <div className="grid gap-4 md:gap-5">
          <FacilityCard
            item={facilityRows[1]}
            image={facilityRows[1].images[1]}
            large
            isMobile={isMobile}
          />

          {/* Hai block dưới */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            <FacilityCard
              item={facilityRows[0]}
              image={facilityRows[0].images[0]}
              isMobile={isMobile}
            />
            <FacilityCard
              item={facilityRows[2]}
              image={facilityRows[2].images[0]}
              isMobile={isMobile}
            />
          </div>

          {/* Ảnh phụ */}
          <div className="grid grid-cols-2 gap-4 md:gap-5">
            <MiniImage src={facilityRows[2].images[1]} alt="" />
            <MiniImage
              src={facilityRows[1].images[0]}
              alt={facilityRows[1].title}
            />
          </div>
        </div>
      </div>
    </section>
  );
};